'use client';
import { CartItem, Money } from '@shared/lib/shopify/types';
import { renderDiscount } from '@shared/lib/utils/discount-utils';
import { renderMoney } from '@shared/lib/utils/money';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

const PriceLine: React.FC<{ label: string; price: number | Money | undefined }> = ({
  label,
  price,
}) => {
  const [animate, setAnimate] = useState(false);
  const [prevPrice, setPrevPrice] = useState(price);

  useEffect(() => {
    if (price !== prevPrice) {
      setAnimate(true);
      // Remove the animation class after a short duration
      const timeout = setTimeout(() => {
        setPrevPrice(price);
      }, 1000);
    }
  }, [price, prevPrice]);

  return (
    <div className="flex justify-between text-sm">
      <p>{label}</p>
      <p
        className={`text-right text-sm transition-transform duration-300 ${animate ? 'scale-105 transform' : ''}`}
      >
        {typeof price === 'number'
          ? renderMoney({ amount: price.toFixed(2), currencyCode: 'USD' })
          : renderMoney(price)}
      </p>
    </div>
  );
};

export default PriceLine;

export function TotalWithDiscount({
  subtotalAmount,
  totalAmount,
}: {
  subtotalAmount: Money;
  totalAmount: Money;
}) {
  if (parseFloat(subtotalAmount.amount) === parseFloat(totalAmount.amount)) {
    return <>{renderMoney(totalAmount)}</>;
  }
  return (
    <>
      {renderMoney(totalAmount)} <s className="text-slate-500">{renderMoney(subtotalAmount)}</s>
    </>
  );
}

export function LineTotalWithDiscount({
  lineItem,
  className,
  discountClassName,
}: {
  lineItem: CartItem;
  className?: string;
  discountClassName?: string;
}) {
  return (
    <>
      <p className={className}>
        <TotalWithDiscount
          subtotalAmount={lineItem.cost.subtotalAmount}
          totalAmount={lineItem.cost.totalAmount}
        />
      </p>
      {lineItem.discountAllocations.length > 0 && (
        <ul
          className={clsx(
            'text-xxs text-bd-primary flex flex-col font-extralight md:text-xs',
            discountClassName
          )}
        >
          {lineItem.discountAllocations.map((discount, idx) => (
            <li key={idx} className="capitalize">
              {renderDiscount(discount!)}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
